/**
 * Track analytics — funciones puras para explotar la data GPS de un traslado.
 *
 * Sin dependencias de React ni Leaflet: enriquece los puntos una sola vez y de
 * ahí derivan estadísticas de viaje, detección de paradas, perfil de velocidad,
 * segmentos coloreados y el emparejado foto→punto. Pensado para vivir en un
 * `useMemo` y ser reutilizado por el detalle histórico y el monitoreo en vivo.
 */
import type { ShipmentLocationPointResponse, ShipmentPhotoResponse } from '../api/models/response'
import { parseUtcDate } from './dates'

// ── Umbrales de clasificación (alineados con el HeatmapLayer) ──
export const STOP_SPEED_KMH = 5 // < 5 km/h = detenido
export const SLOW_SPEED_KMH = 20 // 5–20 km/h = lento (posible tráfico)
export const FAST_SPEED_KMH = 40 // ≥ 40 km/h = en ruta

/** Paleta velocidad → color (rojo detenido → verde en ruta). */
export const SPEED_COLORS = {
  stopped: '#c8442f',
  slow: '#F97316',
  medium: '#EAB308',
  fast: '#2a8a87',
} as const

const EARTH_RADIUS_M = 6371000

// ── Tipos ───────────────────────────────────────────────────

export interface EnrichedPoint {
  index: number
  lat: number
  lng: number
  /** epoch ms */
  t: number
  capturedAt: string
  /** velocidad efectiva en km/h (reportada o calculada) */
  speedKmh: number
  /** distancia desde el punto anterior, en metros (0 en el primero) */
  distFromPrevM: number
  /** tiempo desde el punto anterior, en segundos (0 en el primero) */
  dtFromPrevS: number
  altitude: number | null
  accuracy: number | null
}

export interface Stop {
  startIndex: number
  endIndex: number
  lat: number
  lng: number
  startAt: string
  endAt: string
  durationMs: number
  pointCount: number
}

export interface TripStats {
  totalDistanceM: number
  totalDurationMs: number
  /** tiempo en movimiento = total − detenido */
  movingDurationMs: number
  stoppedDurationMs: number
  /** subconjunto del tiempo en movimiento gastado a baja velocidad (tráfico) */
  slowDurationMs: number
  /** velocidad promedio en movimiento (excluye paradas) */
  avgSpeedKmh: number
  /** velocidad promedio sobre el tiempo total */
  avgSpeedOverallKmh: number
  maxSpeedKmh: number
  stopCount: number
  elevationGainM: number
  elevationLossM: number
  minAltitudeM: number | null
  maxAltitudeM: number | null
  pointCount: number
}

export interface SegmentStats extends TripStats {
  fromIndex: number
  toIndex: number
  intermediateStops: Stop[]
}

export interface ProfilePoint {
  index: number
  label: string
  speedKmh: number
  altitudeM: number | null
  cumDistKm: number
}

export interface ColoredSegment {
  positions: [number, number][]
  color: string
}

export interface PhotoWithTime {
  photo: ShipmentPhotoResponse
  /** índice del punto GPS más cercano, o null si la foto no tiene coords */
  nearestIndex: number | null
  /** hora aproximada (capturedAt del punto más cercano) */
  approxAt: string | null
  /** distancia al punto más cercano, en metros */
  approxDistM: number | null
}

export interface StopOptions {
  radiusM?: number
  minDurationMin?: number
  maxGapMin?: number
}

// ── Geometría ───────────────────────────────────────────────

/** Distancia Haversine entre dos coordenadas, en metros. */
export function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// ── Enriquecido ─────────────────────────────────────────────

/**
 * Precalcula por punto: epoch, distancia/tiempo respecto del anterior y
 * velocidad efectiva. Si el punto trae `speed` (m/s) se usa; si no, se calcula
 * con distancia/tiempo (mismo fallback que el heatmap). Guard `dt > 0` evita
 * dividir por cero ante timestamps duplicados.
 */
export function enrichPoints(points: ShipmentLocationPointResponse[]): EnrichedPoint[] {
  return points.map((p, i) => {
    const t = parseUtcDate(p.capturedAt).getTime()
    let distFromPrevM = 0
    let dtFromPrevS = 0
    let speedKmh = 0

    if (i > 0) {
      const prev = points[i - 1]
      distFromPrevM = haversineMeters(prev.latitude, prev.longitude, p.latitude, p.longitude)
      dtFromPrevS = (t - parseUtcDate(prev.capturedAt).getTime()) / 1000
      if (p.speed != null) {
        speedKmh = p.speed * 3.6
      } else if (dtFromPrevS > 0) {
        speedKmh = (distFromPrevM / dtFromPrevS) * 3.6
      }
    } else if (p.speed != null) {
      speedKmh = p.speed * 3.6
    }

    return {
      index: i,
      lat: p.latitude,
      lng: p.longitude,
      t,
      capturedAt: p.capturedAt,
      speedKmh,
      distFromPrevM,
      dtFromPrevS,
      altitude: p.altitude,
      accuracy: p.accuracy,
    }
  })
}

// ── Detección de paradas ────────────────────────────────────

/**
 * Barrido secuencial O(n) "sliding centroid": acumula puntos contiguos mientras
 * estén dentro de `radiusM` del centroide incremental y el hueco temporal no
 * supere `maxGapMin`. Si el cluster dura ≥ `minDurationMin` es una parada.
 */
export function detectStops(enriched: EnrichedPoint[], opts: StopOptions = {}): Stop[] {
  const radiusM = opts.radiusM ?? 35
  const minDurationMs = (opts.minDurationMin ?? 3) * 60000
  const maxGapMs = (opts.maxGapMin ?? 30) * 60000
  const n = enriched.length
  const stops: Stop[] = []

  let i = 0
  while (i < n) {
    let cLat = enriched[i].lat
    let cLng = enriched[i].lng
    let count = 1
    let j = i + 1

    while (j < n) {
      // Señal perdida: no unir dos paradas separadas por un gran salto temporal
      if (enriched[j].t - enriched[j - 1].t > maxGapMs) break
      // El punto se aleja del centroide vigente → fin del cluster
      if (haversineMeters(cLat, cLng, enriched[j].lat, enriched[j].lng) > radiusM) break
      count++
      cLat += (enriched[j].lat - cLat) / count
      cLng += (enriched[j].lng - cLng) / count
      j++
    }

    const endIndex = j - 1
    const durationMs = enriched[endIndex].t - enriched[i].t

    if (endIndex > i && durationMs >= minDurationMs) {
      stops.push({
        startIndex: i,
        endIndex,
        lat: cLat,
        lng: cLng,
        startAt: enriched[i].capturedAt,
        endAt: enriched[endIndex].capturedAt,
        durationMs,
        pointCount: endIndex - i + 1,
      })
      i = j
    } else {
      i += 1
    }
  }

  return stops
}

// ── Estadísticas ────────────────────────────────────────────

function emptyStats(): TripStats {
  return {
    totalDistanceM: 0,
    totalDurationMs: 0,
    movingDurationMs: 0,
    stoppedDurationMs: 0,
    slowDurationMs: 0,
    avgSpeedKmh: 0,
    avgSpeedOverallKmh: 0,
    maxSpeedKmh: 0,
    stopCount: 0,
    elevationGainM: 0,
    elevationLossM: 0,
    minAltitudeM: null,
    maxAltitudeM: null,
    pointCount: 0,
  }
}

/**
 * Estadísticas agregadas sobre un array enriquecido (completo o un slice). El
 * primer elemento se trata como "entrada" al rango: su distancia/tiempo previo
 * no se cuenta, por lo que funciona igual para el viaje completo y para un
 * tramo A→B. `stops` solo se usa para `stopCount`.
 */
export function computeTripStats(enriched: EnrichedPoint[], stops: Stop[] = []): TripStats {
  const n = enriched.length
  if (n === 0) return emptyStats()

  let totalDistanceM = 0
  let stoppedMs = 0
  let slowMs = 0
  let maxSpeedKmh = 0
  let elevationGainM = 0
  let elevationLossM = 0
  let minAltitudeM: number | null = null
  let maxAltitudeM: number | null = null

  for (let k = 0; k < n; k++) {
    const p = enriched[k]

    if (p.altitude != null) {
      minAltitudeM = minAltitudeM == null ? p.altitude : Math.min(minAltitudeM, p.altitude)
      maxAltitudeM = maxAltitudeM == null ? p.altitude : Math.max(maxAltitudeM, p.altitude)
      const prevAlt = k > 0 ? enriched[k - 1].altitude : null
      if (k > 0 && prevAlt != null) {
        const delta = p.altitude - prevAlt
        if (delta > 0) elevationGainM += delta
        else elevationLossM += -delta
      }
    }

    // Velocidad máxima: descartar segmentos < 2s (jitter del GPS calculado)
    if (p.speedKmh > maxSpeedKmh && (k === 0 || p.dtFromPrevS >= 2)) {
      maxSpeedKmh = p.speedKmh
    }

    if (k === 0) continue

    const dtMs = p.dtFromPrevS * 1000
    totalDistanceM += p.distFromPrevM
    if (p.speedKmh < STOP_SPEED_KMH) stoppedMs += dtMs
    else if (p.speedKmh < SLOW_SPEED_KMH) slowMs += dtMs
  }

  const totalDurationMs = enriched[n - 1].t - enriched[0].t
  const movingDurationMs = Math.max(0, totalDurationMs - stoppedMs)
  const km = totalDistanceM / 1000
  const avgSpeedKmh = movingDurationMs > 0 ? km / (movingDurationMs / 3600000) : 0
  const avgSpeedOverallKmh = totalDurationMs > 0 ? km / (totalDurationMs / 3600000) : 0

  return {
    totalDistanceM,
    totalDurationMs,
    movingDurationMs,
    stoppedDurationMs: stoppedMs,
    slowDurationMs: slowMs,
    avgSpeedKmh,
    avgSpeedOverallKmh,
    maxSpeedKmh,
    stopCount: stops.length,
    elevationGainM,
    elevationLossM,
    minAltitudeM,
    maxAltitudeM,
    pointCount: n,
  }
}

/** Estadísticas de un tramo A→B (índices en cualquier orden). */
export function computeSegmentStats(
  enriched: EnrichedPoint[],
  stops: Stop[],
  a: number,
  b: number,
): SegmentStats {
  const fromIndex = Math.min(a, b)
  const toIndex = Math.max(a, b)
  const slice = enriched.slice(fromIndex, toIndex + 1)
  const intermediateStops = stops.filter((s) => s.startIndex >= fromIndex && s.endIndex <= toIndex)
  const base = computeTripStats(slice, intermediateStops)
  return { ...base, fromIndex, toIndex, intermediateStops }
}

// ── Perfil y polyline coloreada ─────────────────────────────

/** Serie para los charts: velocidad, altitud y distancia acumulada por punto. */
export function buildSpeedProfile(enriched: EnrichedPoint[]): ProfilePoint[] {
  let cumM = 0
  return enriched.map((p) => {
    cumM += p.distFromPrevM
    return {
      index: p.index,
      label: formatClock(p.capturedAt),
      speedKmh: Math.round(p.speedKmh * 10) / 10,
      altitudeM: p.altitude,
      cumDistKm: Math.round((cumM / 1000) * 100) / 100,
    }
  })
}

/** Color de un tramo según su velocidad. */
export function speedColor(kmh: number): string {
  if (kmh < STOP_SPEED_KMH) return SPEED_COLORS.stopped
  if (kmh < SLOW_SPEED_KMH) return SPEED_COLORS.slow
  if (kmh < FAST_SPEED_KMH) return SPEED_COLORS.medium
  return SPEED_COLORS.fast
}

/**
 * Agrupa puntos contiguos del mismo color en pocas polylines (no una por par),
 * para que la ruta coloreada por velocidad escale a miles de puntos.
 */
export function buildColoredSegments(enriched: EnrichedPoint[]): ColoredSegment[] {
  if (enriched.length < 2) return []
  const segments: ColoredSegment[] = []
  let current: ColoredSegment | null = null

  for (let i = 1; i < enriched.length; i++) {
    const color = speedColor(enriched[i].speedKmh)
    const a: [number, number] = [enriched[i - 1].lat, enriched[i - 1].lng]
    const b: [number, number] = [enriched[i].lat, enriched[i].lng]
    if (current && current.color === color) {
      current.positions.push(b)
    } else {
      current = { color, positions: [a, b] }
      segments.push(current)
    }
  }

  return segments
}

// ── Fotos ───────────────────────────────────────────────────

/**
 * Empareja cada foto con el punto GPS más cercano por coordenadas y le asigna
 * su `capturedAt` como hora aproximada. Permite ubicar la foto en el tiempo y
 * en la ruta sin un timestamp propio en el backend.
 */
export function matchPhotosToPoints(
  photos: ShipmentPhotoResponse[],
  enriched: EnrichedPoint[],
): PhotoWithTime[] {
  return photos.map((photo) => {
    if (photo.latitude == null || photo.longitude == null || enriched.length === 0) {
      return { photo, nearestIndex: null, approxAt: null, approxDistM: null }
    }
    let bestIndex = 0
    let bestDist = Infinity
    for (const p of enriched) {
      const d = haversineMeters(photo.latitude, photo.longitude, p.lat, p.lng)
      if (d < bestDist) {
        bestDist = d
        bestIndex = p.index
      }
    }
    return {
      photo,
      nearestIndex: bestIndex,
      approxAt: enriched[bestIndex].capturedAt,
      approxDistM: bestDist,
    }
  })
}

// ── Formatters ──────────────────────────────────────────────

/** "1h 23m", "12m 5s", "45s". */
export function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

/** "1.20 km" / "850 m". */
export function formatDistance(m: number): string {
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`
  return `${Math.round(m)} m`
}

function formatClock(iso: string): string {
  return parseUtcDate(iso).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
}
